/*
 * @Description:
 * @Date: 2024-03-15 18:24:40
 */
import {
  BaseButton,
  XrkTableProps,
  createDialog,
  BaseTextContent,
  BaseAlert
} from 'xrk-components';
import _TableColumsSetVue from './table-columns-set.vue';
import { TabaleColumnType } from '../../data-source/types';
import { storage } from '@/utils/storage';
import { Ref, ref } from 'vue';
import { cloneDeep } from 'lodash';

export const TableColumsSetVue = _TableColumsSetVue;

type PromiseReturnType<TFunction> = TFunction extends (
  ...args: any[]
) => Promise<infer R>
  ? R
  : any;

export function createTableConfig<T extends XrkTableProps>(params: {
  serachConfig: T['searchConfig'];
  tableConifg: T['tableConfig'];
  getLocalStorageKey: () => string;
  initTableColumns?: () => Promise<
    Array<TabaleColumnType<T['tableConfig']['tableColumns'][number]['prop']>>
  >;
  tableDefaultColumnConfig?: {
    [k in T['tableConfig']['tableColumns'][number]['prop']]?: T['tableConfig']['tableColumns'][number];
  };
  searchDefaultConfig?: {
    [k in T['searchConfig']['searchColumns'][number]['prop']]?: T['searchConfig']['searchColumns'][number];
  };
  callback?: (changeKeys: Array<any>) => void;
  excludeSearchKeys?: Array<T['searchConfig']['searchColumns'][number]['prop']>;
}) {
  const {
    serachConfig,
    tableConifg,
    initTableColumns = () => [],
    tableDefaultColumnConfig = {},
    searchDefaultConfig = {},
    getLocalStorageKey,
    callback,
    excludeSearchKeys
  } = params;
  const tableInsertPos = tableConifg.tableColumns.findIndex(
    i => i.prop === '_insert'
  );

  const _searchConfig = ref(cloneDeep(serachConfig)) as Ref<
    typeof serachConfig
  >;
  const _tableConifg = ref(cloneDeep(tableConifg)) as Ref<typeof tableConifg>;

  const {
    oldTableColumns,
    allTableColumns,
    tableKey,
    setLocalTableColumns,
    setAllTableColumns
  } = (initTableColumns => {
    const allTableColumns = ref<PromiseReturnType<typeof initTableColumns>>([]);
    const oldTableColumns = ref<any>([]);
    const tableKey = ref(1);

    return {
      oldTableColumns,
      allTableColumns,
      tableKey,
      setAllTableColumns: async (useInit: boolean) => {
        const data = await initTableColumns();
        const _localMap = new Map();
        const localTableColumns = storage.get(getLocalStorageKey()) || [];
        if (Array.isArray(localTableColumns)) {
          localTableColumns.forEach((item: any) => {
            _localMap.set(item.id, item);
          });
        }
        if (useInit) {
          allTableColumns.value = cloneDeep(data) as any;
          tableKey.value += 1;
        } else {
          allTableColumns.value = cloneDeep(data).map(item => {
            return _localMap.has(item.id)
              ? { ..._localMap.get(item.id) }
              : item;
          });
        }
        allTableColumns.value.sort((a, b) => a.sort - b.sort);
      },
      setLocalTableColumns: (
        res: PromiseReturnType<typeof initTableColumns>
      ) => {
        const _localStorageKey = getLocalStorageKey();
        oldTableColumns.value = storage.get(_localStorageKey) || [];
        storage.set(_localStorageKey, res);
      }
    };
  })(initTableColumns);

  const createTableColumsSet = async () => {
    await setAllTableColumns(false);
    const TableColumsSetRef = ref<any>();
    const MAX_COLUMNS = 10;
    return new Promise<PromiseReturnType<typeof initTableColumns>>(resolve => {
      createDialog({
        width: 960,
        title: '自定义显示列项',
        contentRender: () => (
          <>
            <BaseTextContent>
              <BaseAlert
                style={{ marginBottom: '24px' }}
                title={`最多支持自定义${MAX_COLUMNS}个列项，灰色选中列不支持隐藏和排序`}
                type="warning"
                closable={false}
                show-icon
              ></BaseAlert>
              <TableColumsSetVue
                maxColunms={MAX_COLUMNS}
                key={tableKey.value}
                ref={TableColumsSetRef}
                data={allTableColumns.value}
              />
            </BaseTextContent>
          </>
        ),
        footerRender: done => {
          return (
            <>
              <div>
                <BaseButton
                  type="default"
                  click={async () => {
                    await setAllTableColumns(true);
                  }}
                >
                  恢复默认
                </BaseButton>
                <BaseButton
                  type="default"
                  click={() => {
                    done();
                  }}
                >
                  取消
                </BaseButton>
                <BaseButton
                  type="primary"
                  click={() => {
                    const res = TableColumsSetRef.value?.getAllData();
                    setLocalTableColumns(res);
                    resolve(res);
                    done();
                  }}
                >
                  确定
                </BaseButton>
              </div>
            </>
          );
        }
      });
    });
  };

  const setTable = async () => {
    await setAllTableColumns(false);
    const { searchColumns, tableColumns, newSearchKeys } =
      allTableColumns.value.reduce(
        (pre, cur) => {
          cur.isSearch &&
            cur.isShow &&
            pre.searchColumns.push(cur) &&
            pre.newSearchKeys.push(cur.key);
          cur.isShow && pre.tableColumns.push(cur);
          return pre;
        },
        { searchColumns: [], tableColumns: [], newSearchKeys: [] }
      );
    const { oldSearchKeys } = oldTableColumns.value.reduce(
      (pre, cur) => {
        if (!cur.disabled) {
          cur.isSearch && cur.isShow && pre.oldSearchKeys.push(cur.key);
        }
        return pre;
      },
      { oldSearchKeys: [] }
    );
    _tableConifg.value.tableColumns = [
      ...tableConifg.tableColumns.slice(0, tableInsertPos),
      ...tableColumns.map(({ label, key }) => {
        // 解决表格列顺序变化后 视图不更新的问题
        const _key_ext = `${Date.now()}`;
        const _key = `${_key_ext}${key}`;
        return (
          tableDefaultColumnConfig[key] || {
            label: label,
            prop: _key,
            textLineClamp: 2,
            formatter: ({ row }) => row[_key.replace(_key_ext, '')]
          }
        );
      }),
      ...tableConifg.tableColumns.slice(tableInsertPos + 1)
    ];

    _searchConfig.value.searchColumns = [
      ...serachConfig.searchColumns,
      ...searchColumns
        .filter(({ key }) => !excludeSearchKeys.includes(key))
        .map(
          ({ label, key }) =>
            searchDefaultConfig[key] || {
              type: 'input',
              prop: key,
              style: { width: '180px' },
              config: {
                placeholder: `请输入${label}`,
                maxlength: 20,
                clearable: true
              }
            }
        )
    ];

    const newSearchKeysSet = new Set(newSearchKeys);

    callback?.([
      ...oldSearchKeys.reduce((pre, cur) => {
        if (newSearchKeysSet.has(cur)) {
          newSearchKeysSet.delete(cur);
        } else {
          pre.push(cur);
        }
        return pre;
      }, []),
      ...Array.from(newSearchKeysSet)
    ]);
  };

  const setTableColumns = async () => {
    await createTableColumsSet();
    await setTable();
  };

  setTable();

  return {
    searchConfig: _searchConfig,
    tableConfig: _tableConifg,
    setTableColumns: setTableColumns,
    setTable: setTable
  };
}
